import React, { Fragment, useEffect } from "react";

import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
} from "react-table";

import { Table, Row, Col } from "reactstrap";

import { Link } from "react-router-dom";
import { DataTableProps, splitNumberIntoPages } from "./DataTable.constants";
import { DefaultColumnFilter, Filter } from "./Filters";
import {
  filterSelector,
  filterSelectorProps,
} from "../../actions/LayoutMode.actions";
import { useRecoilState } from "recoil";

interface GlobalFilterProps {
  preGlobalFilteredRows: any;
  globalFilter?: any;
  setGlobalFilter: any;
}

function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}: GlobalFilterProps) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const [filterState, setFilterState] = useRecoilState(filterSelector);

  const onChange = useAsyncDebounce((value: any) => {
    setGlobalFilter(value || undefined);

    setFilterState((prevState: filterSelectorProps) => ({
      ...prevState,
      globalSearch: value,
    }));
  }, 600);

  useEffect(() => {
    if (filterState.globalSearch.length) {
      setValue(filterState.globalSearch);
      setGlobalFilter(filterState.globalSearch || undefined);
    }
  }, []);

  return (
    <Col sm={4}>
      <div className="search-box mb-0 d-inline-block">
        <div className="position-relative">
          <label htmlFor="search-bar-0" className="search-label">
            <span id="search-bar-0-label" className="sr-only">
              Tabloda Ara
            </span>
            <input
              onChange={(e) => {
                setValue(e.target.value);
                onChange(e.target.value);
              }}
              id="search-bar-0"
              type="text"
              autoComplete="off"
              className="form-control font-size-13"
              placeholder={`${count} kayıtta...`}
              value={value || ""}
            />
          </label>
          <i className="bx bx-search-alt search-icon"></i>
        </div>
      </div>
    </Col>
  );
}

const caseInsensitiveTextFilter = (rows: any, id: any, filterValue: any) => {
  const normalizedFilterValue = normalizeTurkishChars(
    filterValue.toLocaleLowerCase("tr-TR")
  );

  return rows.filter((row: any) => {
    const rowValue = row.values[id];
    return rowValue !== undefined
      ? String(
          normalizeTurkishChars(rowValue.toLocaleLowerCase("tr-TR"))
        ).includes(String(normalizedFilterValue).toLowerCase())
      : true;
  });
};

const normalizeTurkishChars = (text: string) => {
  const charMap: any = {
    ç: "c",
    Ç: "C",
    ğ: "g",
    Ğ: "G",
    ı: "i",
    İ: "I",
    ö: "o",
    Ö: "O",
    ş: "s",
    Ş: "S",
    ü: "u",
    Ü: "U",
  };

  return text.replace(/[çÇğĞıİöÖşŞüÜ]/g, (match) => charMap[match]);
};

const globalFilter = (rows: any, id: any, filterValue: any) => {
  return rows.filter((row: any) => {
    const rowValues = Object.values(row.values).join(" ");
    const normalizedRowValues = normalizeTurkishChars(
      rowValues.toLocaleLowerCase("tr-TR")
    );
    const normalizedFilterValue = normalizeTurkishChars(
      filterValue.toLocaleLowerCase("tr-TR")
    );

    return normalizedRowValues.includes(normalizedFilterValue);
  });
};

const DataTable = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  className,
  theadClass,
  isBordered,
  setFilterState,
  pageCountTable,
  pageIndexList,
  hasCustomPagination,
  canNextPageCustom,
  canPreviusPageCustom,
}: DataTableProps) => {
  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
      filter: caseInsensitiveTextFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      globalFilter,
      initialState: { pageIndex: pageIndexList || 0, pageSize: customPageSize },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const isMobile: boolean = /iPhone|iPad|iPod|Android/i.test(
    navigator.userAgent
  );

  const generateSortingIndicator = (column: any) => {
    return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : "";
  };

  const onChangeInSelect = (event: any) => {
    setPageSize(Number(event.target.value));
  };

  const onChangeInInput = (event: any) => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };

  const startIndex = Math.max(
    hasCustomPagination ? (pageIndexList || 0) - 2 : pageIndex - 2,
    0
  );
  const endIndex = Math.min(
    startIndex + 5,
    hasCustomPagination ? pageCountTable : pageOptions.length
  );

  return (
    <Fragment>
      <Row
        className={`mb-2 ${
          isMobile ? "justify-content-center" : "justify-content-between"
        } `}
      >
        <Row>
          {/* {isGlobalFilter && (
            <Col sm={12} md={6}>
              <label className="d-flex">
                <span className="pt-1 fw-normal">Toplam</span>
                <Col sm={12} lg={1} style={{ width: 58 }} className="ms-1 me-1">
                  <select
                    className="custom-select me-2 pe-3 custom-select-sm form-control form-control-sm form-select form-select-sm"
                    value={pageSize}
                    onChange={onChangeInSelect}
                  >
                    {[10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                </Col>
                <span className="pt-1 fw-normal">göster</span>
              </label>
            </Col>
          )} */}
          <Col sm={12} md={6}></Col>

          {isGlobalFilter && (
            <Col sm={12} md={6}>
              <label className="d-flex justify-content-end fw-normal">
                <span className="pt-2">Arama:</span>
                <GlobalFilter
                  preGlobalFilteredRows={preGlobalFilteredRows}
                  globalFilter={state.globalFilter}
                  setGlobalFilter={setGlobalFilter}
                />
              </label>
            </Col>
          )}
        </Row>
      </Row>

      <div className="table-responsive">
        <Table
          hover
          {...getTableProps()}
          className={className}
          bordered={isBordered}
        >
          <thead
            className={theadClass}
            // className="table-light table-nowrap"
          >
            {headerGroups.map((headerGroup: any) => (
              <tr key={headerGroup?.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup?.headers.map((column: any) => (
                  <th key={column?.id}>
                    <div className="mb-2" {...column.getSortByToggleProps()}>
                      {column.render("Header")}
                      {generateSortingIndicator(column)}
                    </div>
                    <Filter column={column} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row: any) => {
              prepareRow(row);
              return (
                <Fragment key={row?.getRowProps().key}>
                  <tr>
                    {row?.cells.map((cell: any) => {
                      return (
                        <td key={cell.id} {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>

      <Row className="align-items-center g-3 text-center text-sm-start">
        <div className="col-sm">
          <div>
            Toplam <span className="fw-normal">{data?.length}</span> sonuçtan{" "}
            <span className="fw-normal ms-1">{page?.length} </span>
            tanesi gösteriliyor
          </div>
        </div>
        <div className="col-sm-auto">
          <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
            <li
              className={
                (hasCustomPagination ? !canPreviusPageCustom : !canPreviousPage)
                  ? "page-item disabled"
                  : "page-item"
              }
            >
              <Link
                to="#"
                className="page-link"
                onClick={() => {
                  if (hasCustomPagination && setFilterState && pageIndexList) {
                    setFilterState((prevState: filterSelectorProps) => ({
                      ...prevState,
                      pageIndex: pageIndexList - 1,
                    }));
                  } else {
                    previousPage();
                  }
                }}
              >
                Geri
              </Link>
            </li>
            {(hasCustomPagination
              ? splitNumberIntoPages(pageCountTable || 0, pageSize)
              : pageOptions
            )
              .slice(startIndex, endIndex)
              .map((item: any, key: number) => (
                <React.Fragment key={key}>
                  <li className="page-item">
                    <Link
                      to="#"
                      className={
                        (hasCustomPagination ? pageIndexList : pageIndex) ===
                        item
                          ? "page-link active"
                          : "page-link"
                      }
                      onClick={() => {
                        gotoPage(item);
                        setFilterState &&
                          setFilterState((prevState: filterSelectorProps) => ({
                            ...prevState,
                            pageIndex: item,
                          }));
                      }}
                    >
                      {item + 1}
                    </Link>
                  </li>
                </React.Fragment>
              ))}
            <li
              className={
                (hasCustomPagination ? !canNextPageCustom : !canNextPage)
                  ? "page-item disabled"
                  : "page-item"
              }
            >
              <Link
                to="#"
                className="page-link"
                onClick={() => {
                  if (hasCustomPagination && setFilterState && pageIndexList) {
                    setFilterState((prevState: filterSelectorProps) => ({
                      ...prevState,
                      pageIndex: pageIndexList + 1,
                    }));
                  } else {
                    nextPage();
                  }
                }}
              >
                İleri
              </Link>
            </li>
          </ul>
        </div>
      </Row>
    </Fragment>
  );
};

export default DataTable;
